import type { CSSProperties } from 'react'
import type { LeaveDetailPayload } from '@lms/shared-types'
import { displayName } from '../../lib/display-name'

/**
 * "Approver path" stepper for the leave-detail modal.
 *
 * The approval chain is fixed and flat (see services/api domain/approvers.py):
 *   Requester → Manager → (Executive Director, only when escalated).
 * A leave escalates when it is unpaid, overlaps a blackout, or otherwise needs
 * Exec-Director sign-off; the manager then *endorses* upward rather than
 * deciding. This component reflects the REAL lifecycle state derived from the
 * leave's `status` + audit `timeline` — not a mock — so an approver can see at
 * a glance who has acted, who is deciding now, and who is still to come.
 */

type ApproverRole = 'employee' | 'manager' | 'exec_director'
type StepState = 'done' | 'current' | 'upcoming' | 'rejected'

export interface ApproverStep {
  name: string
  role: ApproverRole
  state: StepState
  /** Appended to the role label, e.g. "APPROVER · ENDORSED". */
  qualifier?: string
}

const ROLE_LABEL: Record<ApproverRole, string> = {
  employee: 'Requester',
  // Whoever the approval chain assigns, not necessarily a Manager-role user.
  manager: 'Approver',
  exec_director: 'Exec Director',
}

// Statuses where the request is sitting with the manager (step 1) awaiting
// their action — decide (normal) or recommend/endorse (escalated).
const AWAITING_MANAGER = new Set([
  'pending',
])

// Statuses where the request is sitting with the Exec Director (step 2).
const AWAITING_DIRECTOR = new Set(['escalated'])

function firstEvent(
  leave: LeaveDetailPayload,
  actions: readonly string[],
): LeaveDetailPayload['timeline'][number] | undefined {
  return leave.timeline.find((t) => actions.includes(t.action))
}

function lastDecision(
  leave: LeaveDetailPayload,
): LeaveDetailPayload['timeline'][number] | undefined {
  return [...leave.timeline]
    .reverse()
    .find((t) => t.action === 'leave.approved' || t.action === 'leave.rejected')
}

/**
 * Derive the ordered approver chain with per-node lifecycle state from the
 * leave payload. Pure + exported so it can be unit-tested independently of the
 * view. Returns 2 nodes for the normal manager path, 3 when the request is (or
 * was) escalated to the Exec Director.
 */
export function deriveApproverPath(leave: LeaveDetailPayload): ApproverStep[] {
  const status = leave.status.toLowerCase()
  const approved = status === 'approved'
  const rejected = status === 'rejected'

  const escalatedEvt = firstEvent(leave, ['leave.escalated'])
  const recommendedEvt = firstEvent(leave, ['leave.manager_recommended'])
  const decision = lastDecision(leave)
  const decisionRole = decision?.actor_role?.toLowerCase() ?? null
  // An Exec Director who IS the requester's primary approver decides on the
  // normal path — their decision is the approver-node decision, not evidence
  // of an escalation. Without this, a direct report of the ED renders a
  // phantom "Approver · Endorsed" hop by the same person ahead of the ED node.
  const primaryApproverName = leave.employee.primary_approver_name ?? null
  const decidedAsPrimary =
    decision?.actor_name != null &&
    primaryApproverName != null &&
    decision.actor_name.trim().toLowerCase() ===
      primaryApproverName.trim().toLowerCase()
  const decidedByDirector =
    (decisionRole === 'exec_director' || decisionRole === 'director') &&
    !decidedAsPrimary

  // Whether this leave is on the 3-node Exec-Director path. The "should it
  // escalate" decision is NOT re-derived here — it comes from the server's
  // canonical verdict (`escalation_required`, from
  // domain/escalation.py::leave_requires_escalation), so the stepper can never
  // drift from the rule engine. The lifecycle terms only cover "already
  // happened" so a leave whose escalation trigger has since lapsed (e.g. a
  // blackout later removed) still renders the director node it actually went
  // through.
  const isEscalated =
    leave.escalation_required === true ||
    AWAITING_DIRECTOR.has(status) ||
    Boolean(escalatedEvt || recommendedEvt) ||
    (Boolean(decision) && decidedByDirector)

  // ── Requester ──────────────────────────────────────────────────────────
  const requester: ApproverStep = {
    name: displayName(leave.employee),
    role: 'employee',
    state: 'done',
  }

  // ── Manager ────────────────────────────────────────────────────────────
  const managerName =
    escalatedEvt?.actor_name ??
    recommendedEvt?.actor_name ??
    (decision && !decidedByDirector ? decision.actor_name : null) ??
    (leave.next_reviewer?.role === 'manager' ? leave.next_reviewer.name : null) ??
    leave.employee.primary_approver_name ??
    ROLE_LABEL.manager

  // The manager has provably acted (or the flow has moved past them) when an
  // endorsement/recommendation event fired, the request is parked with the
  // Exec Director, or the ED decided it. Reaching the director implies the
  // manager already passed it up, even when the escalation event isn't in the
  // visible timeline. An unpaid leave sitting at `pending` has NOT been
  // endorsed yet — it's still awaiting the manager.
  const managerActed =
    Boolean(escalatedEvt || recommendedEvt) ||
    (decision != null && decidedByDirector) ||
    AWAITING_DIRECTOR.has(status) ||
    (approved && isEscalated)

  let managerState: StepState
  if (rejected && decision && !decidedByDirector) {
    managerState = 'rejected'
  } else if (managerActed || (approved && !isEscalated)) {
    // Endorsed upward, decided the normal path, or moved past to the ED.
    managerState = 'done'
  } else if (AWAITING_MANAGER.has(status)) {
    // Still sitting with the manager — normal decide, or endorse on the
    // escalated path. NOT yet endorsed.
    managerState = 'current'
  } else {
    managerState = 'upcoming'
  }

  // On the escalated path the manager endorses rather than decides; label the
  // node so it doesn't read as the final sign-off.
  const managerQualifier = !isEscalated
    ? undefined
    : managerState === 'done'
      ? 'Endorsed'
      : 'Endorses'

  const manager: ApproverStep = {
    name: managerName,
    role: 'manager',
    state: managerState,
    ...(managerQualifier ? { qualifier: managerQualifier } : {}),
  }

  if (!isEscalated) return [requester, manager]

  // ── Executive Director ─────────────────────────────────────────────────
  const directorName =
    (decision && decidedByDirector ? decision.actor_name : null) ??
    (leave.next_reviewer?.role === 'exec_director'
      ? leave.next_reviewer.name
      : null) ??
    ROLE_LABEL.exec_director

  let directorState: StepState
  if (rejected && decidedByDirector) {
    directorState = 'rejected'
  } else if (approved) {
    directorState = 'done'
  } else if (AWAITING_DIRECTOR.has(status)) {
    directorState = 'current'
  } else {
    directorState = 'upcoming'
  }

  const director: ApproverStep = {
    name: directorName,
    role: 'exec_director',
    state: directorState,
  }

  return [requester, manager, director]
}

// ── View ──────────────────────────────────────────────────────────────────

const STATE_AVATAR_BG: Record<StepState, string> = {
  done: 'var(--success)',
  current: 'var(--steel-blue)',
  upcoming: 'var(--text-3)',
  rejected: 'var(--danger)',
}

/** Glyph shown in the avatar for terminal states; initials otherwise. */
const STATE_GLYPH: Partial<Record<StepState, string>> = {
  done: '✓',
  rejected: '✕',
}

function initialsOf(name: string): string {
  return (
    name
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((w) => w[0]?.toUpperCase() ?? '')
      .join('') || '·'
  )
}

function chipBorder(state: StepState): string {
  if (state === 'current') return 'color-mix(in srgb, var(--steel-blue) 42%, var(--border))'
  if (state === 'done') return 'color-mix(in srgb, var(--success) 38%, var(--border))'
  if (state === 'rejected') return 'color-mix(in srgb, var(--danger) 42%, var(--border))'
  return 'var(--border)'
}

interface ApproverChipProps {
  step: ApproverStep
  /** Display text override — "You" when this node is the viewer. The avatar
   *  still uses the real name's initials so the person stays recognisable. */
  label?: string
}

function ApproverChip({ step, label }: ApproverChipProps): JSX.Element {
  const { name, role, state, qualifier } = step
  const text = label ?? name
  const isRequester = role === 'employee'
  const roleLabel = isRequester
    ? null
    : qualifier
      ? `${ROLE_LABEL[role]} · ${qualifier}`
      : ROLE_LABEL[role]
  const glyph = STATE_GLYPH[state]

  const chipStyle: CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 7,
    padding: '5px 11px 5px 6px',
    borderRadius: 999,
    background: 'var(--surface)',
    border: `1px solid ${chipBorder(state)}`,
    fontSize: 12.5,
    fontWeight: 600,
    color: state === 'upcoming' ? 'var(--text-3)' : 'var(--text)',
    opacity: state === 'upcoming' ? 0.7 : 1,
    whiteSpace: 'nowrap',
  }

  return (
    <span
      data-testid="approver-path-chip"
      data-role={role}
      data-state={state}
      style={chipStyle}
    >
      <span
        aria-hidden="true"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 20,
          height: 20,
          borderRadius: 999,
          background: STATE_AVATAR_BG[state],
          color: 'white',
          fontSize: glyph ? 11 : 9,
          fontWeight: 800,
          letterSpacing: '0.02em',
          flex: 'none',
        }}
      >
        {glyph ?? initialsOf(name)}
      </span>
      <span>{text}</span>
      {roleLabel ? (
        <span
          style={{
            fontSize: 9.5,
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            color: 'var(--text-3)',
          }}
        >
          {roleLabel}
        </span>
      ) : null}
    </span>
  )
}

interface ApproverPathProps {
  leave: LeaveDetailPayload
  /** True when the viewer is the requester — their origin node reads "You". */
  isOwnLeave?: boolean
  /** Viewer's display name — any node that is the viewer reads "You" (covers
   *  an approver viewing a request they're on the chain for). */
  viewerName?: string | null
}

/** Does this step represent the current viewer? The requester node matches on
 *  `isOwnLeave`; approver nodes match on name (the only identifier the payload
 *  carries for them). */
function isViewer(
  step: ApproverStep,
  isOwnLeave: boolean,
  viewerName: string | null,
): boolean {
  if (step.role === 'employee') return isOwnLeave
  if (!viewerName) return false
  return step.name.trim().toLowerCase() === viewerName.trim().toLowerCase()
}

/** Approver-path stepper — the fixed approval chain with live per-node state. */
export function ApproverPath({
  leave,
  isOwnLeave = false,
  viewerName = null,
}: ApproverPathProps): JSX.Element {
  const steps = deriveApproverPath(leave)
  return (
    <div
      data-testid="approver-path"
      style={{ display: 'flex', flexDirection: 'column', gap: 8, minWidth: 0 }}
    >
      <span
        style={{
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          color: 'var(--text-2)',
        }}
      >
        Approver path
      </span>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          flexWrap: 'wrap',
          padding: '2px 0',
        }}
      >
        {steps.map((step, idx) => (
          <div
            key={`${step.role}-${idx}`}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
          >
            <ApproverChip
              step={step}
              {...(isViewer(step, isOwnLeave, viewerName) ? { label: 'You' } : {})}
            />
            {idx < steps.length - 1 ? (
              <span
                aria-hidden="true"
                style={{
                  color: step.state === 'done' ? 'var(--success)' : 'var(--text-3)',
                  fontSize: 14,
                }}
              >
                →
              </span>
            ) : null}
          </div>
        ))}
      </div>
    </div>
  )
}
